import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LOCATION_EDGE_OPTIONS } from "../locationTypes";
import {
  fetchPlaceDetailsByPlaceId,
  fetchPlacePredictions,
  hasGoogleMapsKey,
} from "../utils/googlePlaces";

const DEFAULT_RELATION = "LIVES_AT";

export default function LocationPicker({ onAdd, disabled = false }) {
  const [relationType, setRelationType] = useState(DEFAULT_RELATION);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [loadingPlace, setLoadingPlace] = useState(false);
  const [status, setStatus] = useState("");

  const googleEnabled = useMemo(() => hasGoogleMapsKey(), []);

  useEffect(() => {
    if (!googleEnabled) {
      setOptions([]);
      return undefined;
    }
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setOptions([]);
      return undefined;
    }

    let active = true;
    const timeoutId = setTimeout(async () => {
      setLoadingPredictions(true);
      try {
        const rows = await fetchPlacePredictions(trimmed);
        if (!active) return;
        setOptions(rows);
      } catch (_error) {
        if (!active) return;
        setOptions([]);
      } finally {
        if (!active) return;
        setLoadingPredictions(false);
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [googleEnabled, searchValue]);

  const handleSelectPrediction = async (_event, option) => {
    setSelectedPrediction(option);
    setSelectedPlace(null);
    setStatus("");
    if (!option?.placeId) return;

    setLoadingPlace(true);
    try {
      const details = await fetchPlaceDetailsByPlaceId(option.placeId);
      if (!details) {
        setStatus("Could not load place details.");
        return;
      }
      setSelectedPlace(details);
    } catch (_error) {
      setStatus("Could not load place details.");
    } finally {
      setLoadingPlace(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedPlace || !onAdd) return;
    await onAdd({ edgeType: relationType, place: selectedPlace });
    setSearchValue("");
    setOptions([]);
    setSelectedPrediction(null);
    setSelectedPlace(null);
    setStatus("");
  };

  return (
    <Stack spacing={1}>
      {!googleEnabled && (
        <Alert severity="warning">
          Set `REACT_APP_PLACES_API_BROWSER_KEY` or `REACT_APP_GOOGLE_MAPS_API_KEY` to enable Places.
        </Alert>
      )}
      <TextField
        select
        size="small"
        label="Relation"
        value={relationType}
        onChange={(event) => setRelationType(event.target.value)}
        disabled={disabled}
      >
        {LOCATION_EDGE_OPTIONS.map((item) => (
          <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
        ))}
      </TextField>

      <Autocomplete
        size="small"
        options={options}
        getOptionLabel={(option) => option.description || ""}
        value={selectedPrediction}
        inputValue={searchValue}
        onInputChange={(_event, value) => setSearchValue(value)}
        onChange={handleSelectPrediction}
        loading={loadingPredictions}
        disabled={disabled || !googleEnabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search place"
            placeholder="Type an address or place"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingPredictions ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {loadingPlace && <Typography variant="caption">Loading place details…</Typography>}
      {!!status && <Alert severity="error">{status}</Alert>}

      {selectedPlace && (
        <Alert severity="info">
          <div><b>{selectedPlace.name || "Unnamed place"}</b></div>
          <div>{selectedPlace.formattedAddress || "No formatted address"}</div>
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={() => void handleAdd()}
        disabled={disabled || !selectedPlace || loadingPlace}
      >
        Add Location
      </Button>
    </Stack>
  );
}
